// Unknown external effects of a project (decision D41-B2, PRD AC08). An effect that reached an external system without a
// recorded outcome is never replayed on its own. An organization admin checks the remote system and records "it happened"
// (with the provider's reference, when known) or "it did not happen" (performed once on the next run). Everyone else sees
// the list and that an administrator must decide.
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { isResolvableExternalOperationKind, type ExternalOperationResolution, type ExternalOperationView, type ResolvableExternalOperationKind } from "@maestros/contracts";
import { useI18n, type Translate } from "../../i18n/index.tsx";
import type { MessageKey } from "../../i18n/en.ts";
import { listUnknownExternalOperations, resolutionBody, resolveExternalOperation } from "../../api/endpoints-operations.ts";
import { isApiError } from "../../api/client.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { ActionError } from "../../components/ActionError.tsx";
import { Button } from "../../components/Button.tsx";
import { Dialog } from "../../components/Dialog.tsx";
import { TextAreaField, TextField } from "../../components/Field.tsx";
import { Banner, EmptyState, ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";
import { AlertTriangleIcon, CheckIcon, RefreshIcon, XIcon } from "../../components/icons.tsx";
import { useToast } from "../../components/Toast.tsx";

/**
 * Plain-language label of each kind an administrator can resolve; any other kind is shown as its identifier and offers
 * no decision (the API lists and resolves only these).
 */
const KIND_LABEL: Record<ResolvableExternalOperationKind, MessageKey> = {
  "git.commit": "operations.kind.gitCommit",
  "git.push": "operations.kind.gitPush",
  "git.push.publish": "operations.kind.gitPushPublish",
  "git.integrate": "operations.kind.gitIntegrate",
  "pr.create": "operations.kind.prCreate",
  "github.repository.create": "operations.kind.githubRepositoryCreate",
  "github.deploy_key.add": "operations.kind.githubDeployKeyAdd",
  "coolify.app.create": "operations.kind.coolifyAppCreate",
  "coolify.key.create": "operations.kind.coolifyKeyCreate",
  "coolify.deploy": "operations.kind.coolifyDeploy",
};

function kindOf(op: ExternalOperationView, t: Translate): { label: string; technical: string | null } {
  return isResolvableExternalOperationKind(op.kind) ? { label: t(KIND_LABEL[op.kind]), technical: op.kind } : { label: op.kind, technical: null };
}

export interface UnknownOperationsApi {
  list: (projectId: string, signal: AbortSignal) => Promise<ExternalOperationView[]>;
  resolve: typeof resolveExternalOperation;
}

const liveApi: UnknownOperationsApi = { list: listUnknownExternalOperations, resolve: resolveExternalOperation };

interface Pending {
  op: ExternalOperationView;
  outcome: ExternalOperationResolution;
  /** The control that opened the dialog; focus returns to it when the person cancels. */
  opener: HTMLElement | null;
}

export function UnknownOperationsPanel({ projectId, canResolve, api = liveApi }: { projectId: string; canResolve: boolean; api?: UnknownOperationsApi }) {
  const { t, formatDateTime } = useI18n();
  const { toast } = useToast();
  const titleId = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const list = useAsync((signal) => api.list(projectId, signal), [projectId]);
  const [pending, setPending] = useState<Pending | null>(null);
  const items = list.data ?? [];

  const cancel = () => {
    const opener = pending?.opener ?? null;
    setPending(null);
    // After the modal closes: the opener is still on screen when the person cancels.
    window.setTimeout(() => opener?.focus(), 0);
  };

  // The row leaves the list on reload, so focus moves to the heading instead of the removed button.
  const toHeading = () => window.setTimeout(() => titleRef.current?.focus(), 0);

  const resolved = (outcome: ExternalOperationResolution, op: ExternalOperationView) => {
    setPending(null);
    list.reload();
    toast(t(outcome === "done" ? "operations.resolvedDone" : "operations.resolvedNotDone", { operation: kindOf(op, t).label }), outcome === "done" ? "success" : "info");
    toHeading();
  };

  const stale = () => {
    setPending(null);
    list.reload();
    toHeading();
  };

  return (
    <section className="card" aria-labelledby={titleId} data-testid="unknown-operations">
      <div className="card-header">
        <AlertTriangleIcon size={18} />
        <h2 id={titleId} ref={titleRef} tabIndex={-1} className="card-title" style={{ flex: 1 }}>
          {t("operations.title")}
        </h2>
        <Button size="sm" variant="ghost" icon={<RefreshIcon size={14} />} onClick={list.reload}>
          {t("common.refresh")}
        </Button>
      </div>
      <div className="card-body stack">
        <p className="muted">{t("operations.intro")}</p>
        {canResolve ? <p className="muted">{t("operations.introAdmin")}</p> : <Banner tone="info" testId="unknown-operations-read-only">{t("operations.readOnlyNotice")}</Banner>}
        {list.error && !list.data ? (
          <ErrorBanner error={list.error} onRetry={list.reload} title={t("operations.loadFailed")} />
        ) : list.loading ? (
          <LoadingBlock label={t("common.loading")} />
        ) : items.length === 0 ? (
          <EmptyState icon={<CheckIcon />} title={t("operations.emptyTitle")} headingLevel={3}>
            <p>{t("operations.emptyBody")}</p>
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <caption className="sr-only">{t("operations.title")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("operations.col.operation")}</th>
                  <th scope="col">{t("operations.col.destination")}</th>
                  <th scope="col">{t("operations.col.requested")}</th>
                  {canResolve ? <th scope="col">{t("operations.col.decision")}</th> : null}
                </tr>
              </thead>
              <tbody>
                {items.map((op) => {
                  const kind = kindOf(op, t);
                  return (
                    <tr key={op.id} data-testid="unknown-operation-row" data-kind={op.kind}>
                      <td>
                        <span>{kind.label}</span>
                        {kind.technical ? (
                          <span className="subtle mono" style={{ display: "block" }}>
                            {kind.technical}
                          </span>
                        ) : null}
                      </td>
                      <td>{op.destination ? <span className="mono user-content">{op.destination}</span> : <span className="subtle">{t("operations.noDestination")}</span>}</td>
                      <td>
                        <time dateTime={op.created_at}>{formatDateTime(op.created_at)}</time>
                      </td>
                      {canResolve ? (
                        <td>
                          {isResolvableExternalOperationKind(op.kind) ? (
                            <div className="row row--wrap" style={{ gap: 8 }}>
                              <Button
                                size="sm"
                                icon={<CheckIcon size={14} />}
                                aria-label={t("operations.happenedFor", { operation: kind.label })}
                                data-testid="unknown-operation-happened"
                                onClick={(e) => setPending({ op, outcome: "done", opener: e.currentTarget })}
                              >
                                {t("operations.happened")}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                icon={<XIcon size={14} />}
                                aria-label={t("operations.didNotHappenFor", { operation: kind.label })}
                                data-testid="unknown-operation-did-not-happen"
                                onClick={(e) => setPending({ op, outcome: "not_done", opener: e.currentTarget })}
                              >
                                {t("operations.didNotHappen")}
                              </Button>
                            </div>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {pending ? (
        <ResolutionDialog key={`${pending.op.id}-${pending.outcome}`} pending={pending} api={api} onCancel={cancel} onResolved={resolved} onStale={stale} />
      ) : null}
    </section>
  );
}

function ResolutionDialog({
  pending,
  api,
  onCancel,
  onResolved,
  onStale,
}: {
  pending: Pending;
  api: UnknownOperationsApi;
  onCancel: () => void;
  onResolved: (outcome: ExternalOperationResolution, op: ExternalOperationView) => void;
  onStale: () => void;
}) {
  const { t } = useI18n();
  const { op, outcome } = pending;
  const done = outcome === "done";
  const kind = kindOf(op, t);
  const formId = useId();
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const [note, setNote] = useState("");
  const [providerRef, setProviderRef] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // The native dialog focuses its close button on open; the first thing to fill in is the note.
  useEffect(() => {
    noteRef.current?.focus();
  }, []);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitted(true);
    if (note.trim() === "" || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.resolve(op.id, resolutionBody({ outcome, note, externalRef: providerRef }));
      onResolved(outcome, op);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onCancel}
      busy={busy}
      testId="unknown-operation-dialog"
      title={t(done ? "operations.dialog.happenedTitle" : "operations.dialog.didNotHappenTitle")}
      description={t(done ? "operations.dialog.happenedBody" : "operations.dialog.didNotHappenBody")}
      footer={
        <div className="row row--wrap" style={{ justifyContent: "flex-end", gap: 8 }}>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form={formId} variant={done ? "primary" : "danger"} loading={busy} data-testid="unknown-operation-confirm">
            {t(done ? "operations.confirmHappened" : "operations.confirmDidNotHappen")}
          </Button>
        </div>
      }
    >
      <form id={formId} className="stack" onSubmit={submit} noValidate>
        <p>
          <strong>{kind.label}</strong>
          {op.destination ? (
            <>
              {" — "}
              <span className="mono user-content">{op.destination}</span>
            </>
          ) : null}
        </p>
        {error ? (
          <ActionError
            error={error}
            onDismiss={() => setError(null)}
            onReload={isApiError(error) && error.code === "version_conflict" ? onStale : undefined}
          />
        ) : null}
        <TextAreaField
          ref={noteRef}
          label={t("operations.note")}
          hint={t("operations.noteHint")}
          error={submitted && note.trim() === "" ? t("validation.required") : null}
          required
          rows={3}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        {done ? (
          <TextField
            label={t("operations.providerRef")}
            optionalLabel={t("common.optional")}
            hint={t("operations.providerRefHint")}
            maxLength={300}
            value={providerRef}
            onChange={(e) => setProviderRef(e.target.value)}
          />
        ) : null}
      </form>
    </Dialog>
  );
}
